import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { DeleteOutlined, EditOutlined, MoreVertOutlined } from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import { useUpdateAssociatedTestTemplate } from "../../hooks"
import { AssociatedTestTemplateFormDialogContainer } from "../AssociatedTestTemplateFormDialogContainer/AssociatedTestTemplateFormDialogContainer"

import { ConfirmationDialog } from "@/components/common/Modals"
import type { AssociatedTestTemplate } from "@/graphql/codegen/graphql"

interface AssociateTestTemplateRowActionsProps {
  row: CellContext<AssociatedTestTemplate, unknown>["row"]
  onRefresh: () => void
}

const AssociateTestTemplateRowActions: FC<AssociateTestTemplateRowActionsProps> = ({ row, onRefresh }) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)
  const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] = useState(false)

  const { update: remove } = useUpdateAssociatedTestTemplate({
    onSuccess: onRefresh,
    errorMessage: "Failed to remove associated test template.",
    successMessage: "Associated test template successfully removed.",
  })

  const handleDropdownOpen = () => setIsDropdownOpen(true)

  const handleDropdownClose = () => setIsDropdownOpen(false)

  const handleFormDialogOpen = () => {
    setIsDropdownOpen(false)
    setIsFormDialogOpen(true)
  }

  const handleFormDialogClose = () => setIsFormDialogOpen(false)

  const handleFormDialogSubmit = () => {
    onRefresh()
    setIsFormDialogOpen(false)
  }

  const handleRemoveDialogOpen = () => {
    setIsDropdownOpen(false)
    setIsRemoveConfirmationOpen(true)
  }

  const handleRemoveConfirmationClose = () => setIsRemoveConfirmationOpen(false)

  const handleRemoveConfirm = async () => {
    await remove({
      associatedTestTemplateId: row.original.associatedTestTemplateId,
      isDeleted: true,
    })
    setIsRemoveConfirmationOpen(false)
  }

  return (
    <Box className='flex items-center justify-center' ref={anchorElRef}>
      <IconButton onClick={handleDropdownOpen} data-test='row-actions-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isDropdownOpen}
        onClose={handleDropdownClose}
        data-test='row-actions-menu'
      >
        <MenuItem data-test='edit-menu-item' onClick={handleFormDialogOpen}>
          <ListItemIcon>
            <EditOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
          </ListItemIcon>
          <ListItemText primary='Edit Test Template' />
        </MenuItem>
        <MenuItem data-test='remove-menu-item' onClick={handleRemoveDialogOpen}>
          <ListItemIcon>
            <DeleteOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
          </ListItemIcon>
          <ListItemText primary='Remove Test Template' />
        </MenuItem>
      </Menu>
      {isFormDialogOpen && (
        <AssociatedTestTemplateFormDialogContainer
          productId={row.original.product!.productId}
          associatedTestTemplate={row.original}
          onClose={handleFormDialogClose}
          onSubmit={handleFormDialogSubmit}
        />
      )}
      {isRemoveConfirmationOpen && (
        <ConfirmationDialog
          title='Remove Test Template'
          content='Are you sure you want to remove this test template? This action cannot be undone.'
          closeLabel='Cancel'
          confirmLabel='Remove'
          isWarning={true}
          onClose={handleRemoveConfirmationClose}
          onConfirm={handleRemoveConfirm}
        />
      )}
    </Box>
  )
}

export default AssociateTestTemplateRowActions
